import { GrammarCheckCache } from '../cache/GrammarCheckCache';
import { GrammarCheckerConfig } from '../config';
import { GrammarCheckAlert, GrammarCheckResult } from '../types';
import { splitIntoSentences } from '../utils/textUtils';

const grammarCheckCache = new GrammarCheckCache();
let grammarCheckAbortController: AbortController | null = null;

async function builtInCheckText(
  text: string,
  config: GrammarCheckerConfig,
): Promise<GrammarCheckResult> {
  if (!config.apiBaseUrl || !config.getAccessToken) {
    throw new Error(
      'GrammarChecker: apiBaseUrl and getAccessToken are required when checkText is not provided',
    );
  }

  const accessToken = await config.getAccessToken();

  if (grammarCheckAbortController) {
    grammarCheckAbortController.abort(
      'cancelling duplicated grammar check request',
    );
  }

  grammarCheckAbortController = new AbortController();

  const response = await fetch(
    `${config.apiBaseUrl}/api/proxy/linguix/v1/checker`,
    {
      body: JSON.stringify({ text }),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Access-Token': accessToken,
      },
      signal: grammarCheckAbortController.signal,
    },
  );

  const result: GrammarCheckResult = await response.json();

  if (result.status !== 200) {
    throw new Error('Error in grammar check response');
  }

  return result;
}

export async function checkTextWithCache(
  text: string,
  config: GrammarCheckerConfig,
): Promise<GrammarCheckResult> {
  const checkFn = config.checkText
    ? config.checkText
    : (t: string) => builtInCheckText(t, config);

  const sentences = splitIntoSentences(text);
  const uncachedSentences: { sentence: string; offset: number }[] = [];
  const cachedAlerts: GrammarCheckAlert[] = [];

  sentences.forEach(({ sentence, offset }) => {
    const cached = grammarCheckCache.get(sentence);
    if (cached) {
      cachedAlerts.push(
        ...cached.map((alert) => ({
          ...alert,
          absOffset: alert.absOffset + offset,
        })),
      );
    } else {
      uncachedSentences.push({ sentence, offset });
    }
  });

  const newAlerts: GrammarCheckAlert[] = [];
  if (uncachedSentences.length > 0) {
    const uncachedText = uncachedSentences.map((s) => s.sentence).join(' ');
    const result = await checkFn(uncachedText);

    let currentOffset = 0;
    uncachedSentences.forEach(({ sentence, offset }) => {
      const sentenceAlerts = result.alerts
        .filter(
          (alert) =>
            alert.absOffset >= currentOffset &&
            alert.absOffset < currentOffset + sentence.length,
        )
        .map((alert) => ({
          ...alert,
          absOffset: alert.absOffset - currentOffset + offset,
        }));

      grammarCheckCache.set(
        sentence,
        sentenceAlerts.map((alert) => ({
          ...alert,
          absOffset: alert.absOffset - offset,
        })),
      );

      newAlerts.push(...sentenceAlerts);
      currentOffset += sentence.length + 1;
    });
  }

  const allAlerts = [...cachedAlerts, ...newAlerts].sort(
    (a, b) => a.absOffset - b.absOffset,
  );

  return {
    status: 200,
    alerts: allAlerts,
    stats: null,
  };
}

export { grammarCheckCache };
